/// <amd-module name="ExtensionManagement/ManagementHub/ExtensionsService" />

import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";

import { ObservableArray, ObservableValue } from "VSS/Core/Observable";

import { format } from "VSS/Core/Util/String";

import { IVssPageContext, IVssService, Services, TeamFoundationHostType, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssLocationService } from "VSS/Platform/Location";
import { IPageData } from "VSS/Platform/Page";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

import {
    ExtensionAuditLog, ExtensionManifest, ExtensionRequestState,
    ExtensionStateFlags, InstalledExtension
} from "./Contracts/ExtensionManagement";
import {
    ExtensionQuery, ExtensionQueryFilterType, ExtensionQueryFlags,
    ExtensionQueryResult, FilterCriteria, PublishedExtension, PublisherFlags, QueryFilter
} from "./Contracts/Gallery";
import {
    IExtensionManagementHubDefaultServiceData, IExtensionModel, IInstalledExtensionModel, IRequestedExtensionModel
} from "./DataProviderContract";
import { getExtensionManagementClient, IExtensionManagementRestClient } from "./RestClient/ExtensionManagement";
import { getGalleryClient, IGalleryRestClient } from "./RestClient/Gallery";
import {
    IExtensionActionViewModel, IExtensionViewModel,
    IHistoryAction, IInstalledExtensionViewModel, IRequestedExtensionViewModel
} from "./UIContracts";

import * as Resources from "ExtensionManagement/ManagementHub/Resources.ExtManagmentHub";

const galleryServiceInstanceId = "00000029-0000-8888-8000-000000000000";

interface IHeaderActionData {
    /**
     * URL of the marketplace.
     */
    marketplaceUrl?: string;
    suiteHomeUrl?: string;
}

/**
 * Default data needed for extension management hubs and controls
 */
interface ExtensionManagementScopesData {
    scopeDefinitions: { [key: string]: string };
}

const ActionToString: { [auditAction: string]: string } = {
    I: Resources.Installed,
    E: Resources.Enabled,
    D: Resources.Disabled,
    UV: Resources.UpdatedVersionTo,
    UI: Resources.Uninstalled
};

/**
 * Service that provides data and operations necessary in Extension Management
 */
export interface IExtensionsService extends IVssService {
    /**
     * This will open a new tab to extension, admin can install extension to fullfil the request
     * @param extensionRequest - the request to be approved
     */
    installExtension(extensionRequest: IExtensionViewModel): void;

    /**
     * This will delete the request made by the user (only user who created the request can delete it)
     * @param extensionRequest - the request to be deleted
     */
    deleteRequest(extensionRequest: IRequestedExtensionViewModel): void;

    /**
     * This will disable an installed extension, disabled extension can not be used by anyone
     * @param extension - the extension to be disabled
     */
    disableExtension(extension: IInstalledExtensionViewModel): void;

    /**
     * This will enable a already disabled extension
     * @param extension - the extension to be enabled
     */
    enableExtension(extension: IInstalledExtensionViewModel): void;

    /**
     * Gets the value whether the user can manage EMS security or not.
     */
    getCanUserManageSecurity(): boolean;

    /**
     * Gets the value whether or not EMS roles can be assigned to a service principal / managed identity
     */
    getSupportServicePrincipals(): boolean;

    /**
     * Gets if there occured an error when getting data.
     */
    getErrorLoadingData(): boolean;

    /**
     * Returns details of an extension.
     * @param extension - the extension whos history is required
     */
    getExtensionHistory(extension: IExtensionViewModel): ObservableArray<IHistoryAction | ObservableValue<IHistoryAction | undefined>>;

    /**
     * Returns an extension by identifier.
     * @param identifier - the identifier of extension
     */
    getExtensionByIdentifier(identifier: string): IExtensionViewModel;

    /**
     * Returns unique identifier of an extension.
     * @param extension - the extension whos identifier is required
     */
    getExtensionIdentifier(extension: IExtensionViewModel): string;

    /**
     * Returns a URL to gallery (locally avaialble extensions)
     * not avilable for hosted services
     */
    getGalleryWebUrl(): string;

    /**
     * Returns a list of data necessary to display an installed extensions in observable object
     */
    getInstalledExtensionViewModels(): ObservableArray<IInstalledExtensionViewModel>;

    /**
     * Returns true if using hosted service, false if on prem
     */
    getIsHosted(): boolean;

    /**
     * Returns a observable boolean that's true when loading requested extensions
     */
    getIsLoadingRequestedExtensionViewModels(): ObservableValue<boolean>;

    /**
     * Returns true if user is admin
     */
    getIsUserAdmin(): boolean;

    /**
     * Returns link to marketplace
     */
    getMarketPlaceUrl(): string;

    /**
     * Returns data needed for reauthorizing an extension.
     * @param extension - extension to be authorized
     * @return promise for the data
     */
    getNewScopes(extension: IInstalledExtensionViewModel): Promise<string[]>;

    /**
     * Returns an extension by identifier.
     * @param identifier - the identifier of extension
     */
    getRequestedExtensionByIdentifier(identifier: string): IRequestedExtensionViewModel;

    /**
     * Returns a list of data necessary to display an requested extensions
     */
    getRequestedExtensionViewModels(): ObservableArray<IRequestedExtensionViewModel>;

    /**
     * Returns a dictionary of scope ids and their description
     */
    getScopeDefinitions(): { [key: string]: string };

    /**
     * Returns a list of data necessary to display shared extensions
     */
    getSharedExtensionViewModels(): ObservableArray<IExtensionViewModel>;

    /**
     * Returns user id of current user.
     */
    getUserId(): string;

    /**
     * This will authorize extension update with new scopes
     * @param extension an installed extension to be updated
     */
    reauthorizeExtension(extension: IInstalledExtensionViewModel): void;

    /**
     * This will reject a request made by user, the user will get notification with provided reject reason
     * @param extensionRequest - the request to be rejected
     * @param rejectReason - the reason for rejecting the request (this will be sent to the requester)
     */
    rejectRequest(extensionRequest: IRequestedExtensionViewModel, rejectReason: string): void;

    /**
     * This will uninstall extension for everyone
     * @param extension - the extension to be uninstalled
     * @param reason - other reasons if user typed in any
     * @param reasonCode - codes for reasons selected by user
     */
    uninstallExtension(extension: IInstalledExtensionViewModel, reason?: string, reasonCode?: string): void;

    /**
     * This gives an array of formetted scopes based on extensions scope definitions.
     * @param scopes - unformatted scopes from the extension manifest source
     */
    getFormattedScopes(scopes: string[]): string[];
}

/**
 * Service to get and manage extensions data
 */
export class ExtensionsService extends VssService implements IExtensionsService {
    private accountToken: string;
    private auditDataMap: { [extensionIdentifier: string]: ObservableArray<IHistoryAction | ObservableValue<IHistoryAction | undefined>> } = {};
    private collectionUrl: string;
    private errorLoadingData: boolean;
    private extensionManagementRestClient: IExtensionManagementRestClient;
    private extensionsServiceData: IExtensionManagementHubDefaultServiceData;
    private extensionsMap: { [extensionIdentifier: string]: IExtensionViewModel } = {};
    private galleryRootUrl: string;
    private galleryWebUrl: string;
    private galleryRestClient: IGalleryRestClient;
    private hostId: string;
    private installedExtensions: ObservableArray<IInstalledExtensionViewModel>;
    private isHosted: boolean;
    private isLoadingRequestedExtensionsTab: ObservableValue<boolean>;
    private marketplaceUrl: string | undefined;
    private messageService: IGlobalMessagesService;
    private requestedExtensions: ObservableArray<IRequestedExtensionViewModel>;
    private requestedExtensionsMap: { [extensionIdentifier: string]: IRequestedExtensionViewModel } = {};
    private scopeDefinitions: { [key: string]: string };
    private sharedExtensions: ObservableArray<IExtensionViewModel>;
    private user: IdentityRef;

    constructor() {
        super();

        this.isLoadingRequestedExtensionsTab = new ObservableValue<boolean>(true);
        this.extensionsMap = {};
    }

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this.extensionManagementRestClient = getExtensionManagementClient(this.pageContext);
        this.galleryRestClient = getGalleryClient(this.pageContext, { hostType: TeamFoundationHostType.Deployment });

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        const serviceData = contributionService.getData<IExtensionManagementHubDefaultServiceData>(
            "ms.vss-extmgmt-web.extensionManagmentHub-collection-data-provider"
        );

        const headerActionData = contributionService.getData<IHeaderActionData>("ms.vss-tfs-web.header-action-data");

        const pageData = contributionService.getData<IPageData>("ms.vss-web.page-data");

        const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");

        this.messageService = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");

        if (serviceData && pageData && headerActionData) {
            this.errorLoadingData = false;
            this.hostId = pageData.hostId;
            this.user = pageData.user;
            this.accountToken = serviceData.accountToken;
            this.extensionsServiceData = serviceData;
            this.galleryRootUrl = locationService.getServiceLocation(galleryServiceInstanceId, TeamFoundationHostType.Deployment);
            this.galleryWebUrl = combineUrlPaths(this.galleryRootUrl, "/_gallery");
            this.marketplaceUrl = headerActionData.marketplaceUrl;
            this.collectionUrl = headerActionData.suiteHomeUrl ? headerActionData.suiteHomeUrl : this.galleryRootUrl;
            this.isHosted = pageData.isHosted;

            this.buildExtensions();
            this.buildExtensionRequests();
        } else {
            this.errorLoadingData = true;
        }
    }

    public deleteRequest = (extensionRequest: IRequestedExtensionViewModel): void => {
        this.extensionManagementRestClient.deleteRequest(extensionRequest.publisherId, extensionRequest.extensionId).then(() => {
            const index: number = this.requestedExtensions.value.indexOf(extensionRequest);
            if (index > -1) {
                this.requestedExtensions.splice(index, 1);
            }
        });
    };

    public disableExtension = (extension: IInstalledExtensionViewModel): void => {
        if (extension.extensionDisabled.value == false) {
            this.toggleExtensionState(extension);
        }
    };

    public enableExtension = (extension: IInstalledExtensionViewModel): void => {
        if (extension.extensionDisabled.value == true) {
            this.toggleExtensionState(extension);
        }
    };

    public getCanUserManageSecurity = (): boolean => {
        return this.extensionsServiceData.manageSecurityPermission;
    };

    public getSupportServicePrincipals = (): boolean => {
        return this.extensionsServiceData.supportServicePrincipals;
    };

    public getErrorLoadingData = (): boolean => {
        return this.errorLoadingData;
    };

    public getExtensionByIdentifier = (identifier: string): IExtensionViewModel => {
        return this.extensionsMap[identifier];
    };

    public getExtensionHistory = (
        extension: IExtensionViewModel,
        force?: boolean
    ): ObservableArray<IHistoryAction | ObservableValue<IHistoryAction | undefined>> => {
        let history: ObservableArray<IHistoryAction | ObservableValue<IHistoryAction | undefined>>;

        if (!extension) {
            return new ObservableArray<IHistoryAction>([]);
        }

        const identifier = this.getExtensionIdentifier(extension);

        if (this.auditDataMap[identifier] && !force) {
            // if we have cached history and not forced to get new one use that
            history = this.auditDataMap[identifier];
        } else {
            if (this.auditDataMap[identifier]) {
                history = this.auditDataMap[identifier];
            } else {
                const asyncRow = new ObservableValue<IHistoryAction | undefined>(undefined);
                history = new ObservableArray<IHistoryAction | ObservableValue<IHistoryAction | undefined>>([]);
                history.push(asyncRow);
            }

            this.extensionManagementRestClient.getAuditLog(extension.publisherId, extension.extensionId).then((log: ExtensionAuditLog) => {
                let data: IHistoryAction[] = [];

                if (log) {
                    log.entries.reverse().forEach(element => {
                        if (element.auditAction !== "U") {
                            let actionString: string;
                            let userIconUrl: string;
                            let userName: string;
                            if (element.auditAction === "UV") {
                                actionString = format(ActionToString[element.auditAction], element.comment);
                                if (element.updatedBy.displayName) {
                                    userName = element.updatedBy.displayName;
                                    userIconUrl = this.getUserIconUrl(element.updatedBy.id);
                                } else {
                                    userName = this.isHosted ? Resources.AzureDevOpsService : Resources.AzureDevOpsServer;
                                    userIconUrl = "";
                                }
                            } else {
                                actionString = ActionToString[element.auditAction];
                                userName = element.updatedBy.displayName;
                                userIconUrl = this.getUserIconUrl(element.updatedBy.id);
                            }

                            data.push({
                                auditAction: actionString,
                                date: element.auditDate,
                                userId: element.updatedBy.id,
                                userName: userName,
                                userIconUrl: userIconUrl
                            });
                        }
                    });
                }

                history.value = data;
                // store history in map if not in it
                if (!this.auditDataMap[identifier]) {
                    this.auditDataMap[identifier] = history;
                }
            });
        }

        return history;
    };

    public getExtensionIdentifier = (extension: IExtensionViewModel): string => {
        return this.createExtensionIdentifier(extension.publisherId, extension.extensionId);
    };

    public getGalleryWebUrl = (): string => {
        return this.galleryWebUrl;
    };

    public getInstalledExtensionViewModels = (): ObservableArray<IInstalledExtensionViewModel> => {
        return this.installedExtensions;
    };

    public getIsHosted = (): boolean => {
        return this.isHosted;
    };

    public getIsLoadingRequestedExtensionViewModels = (): ObservableValue<boolean> => {
        return this.isLoadingRequestedExtensionsTab;
    };

    public getIsUserAdmin = (): boolean => {
        return this.extensionsServiceData.manageExtensionPermission;
    };

    public getMarketPlaceUrl = (): string => {
        return this.marketplaceUrl ? this.marketplaceUrl : "#";
    };

    public getNewScopes = (extension: IInstalledExtensionViewModel): Promise<string[]> => {
        // fetch latest version's manifest from marketplace
        return fetch(extension.manifestSource)
            .then((response: Response) => {
                return response.json();
            })
            .then((data: ExtensionManifest) => {
                return data.scopes;
            });
    };

    public getRequestedExtensionByIdentifier = (identifier: string): IRequestedExtensionViewModel => {
        return this.requestedExtensionsMap[identifier];
    };

    public getRequestedExtensionViewModels = (): ObservableArray<IRequestedExtensionViewModel> => {
        return this.requestedExtensions;
    };

    public getScopeDefinitions = (): { [key: string]: string } => {
        if (!this.scopeDefinitions) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const scopesData = contributionService.getData<ExtensionManagementScopesData>(
                "ms.vss-extmgmt-web.extensionManagmentHub-collection-scopes-data-provider"
            );
            this.scopeDefinitions = scopesData ? scopesData.scopeDefinitions : {};
        }

        return this.scopeDefinitions;
    };

    public getFormattedScopes(scopes: string[]): string[] {
        const scopeList: string[] = [];
        const scopeDefinitions = this.getScopeDefinitions();

        for (let scope of scopes) {
            if (scopeDefinitions[scope]) {
                scopeList.push(scopeDefinitions[scope]);
            } else {
                scopeList.push(scope);
            }
        }

        if (scopeList.length === 0) {
            scopeList.push(Resources.NoScope);
        }

        return scopeList;
    }

    public getSharedExtensionViewModels = (): ObservableArray<IExtensionViewModel> => {
        return this.sharedExtensions;
    };

    public getUserId = (): string => {
        return this.user.id;
    };

    public installExtension = (extensionRequest: IExtensionViewModel): void => {
        window.open(extensionRequest.extensionInstallPageUrl, "_blank");
    };

    public reauthorizeExtension = (extension: IInstalledExtensionViewModel): void => {
        this.extensionManagementRestClient
            .registerAuthorization(extension.publisherId, extension.extensionId, extension.currentVersionsRegistrationId)
            .then(
                () => {
                    // update data of extension to be approx values
                    extension.reauthorizationRequired = false;
                    extension.version = extension.latestAvailableVersion;
                    extension.lastUpdated = new Date();
                    // Sort by name giving prority to re authorization needed
                    // reassign it so that observer will get notified
                    this.installedExtensions.value = this.installedExtensions.value.sort(this.installedExtensionComparator);

                    // reload history
                    this.getExtensionHistory(extension, true);

                    if (this.messageService) {
                        this.messageService.addToast({
                            message: format(Resources.ReauthorizationSuccessful, extension.extensionName),
                            duration: 3000
                        });
                    }
                },
                () => {
                    if (this.messageService) {
                        this.messageService.addToast({ message: format(Resources.ReauthorizationFailed, extension.extensionName), duration: 3000 });
                    }
                }
            );
    };

    public rejectRequest = (extensionRequest: IRequestedExtensionViewModel, rejectReason: string): void => {
        this.extensionManagementRestClient
            .resolveAllRequests(rejectReason, extensionRequest.publisherId, extensionRequest.extensionId, ExtensionRequestState.Rejected)
            .then(
                () => {
                    extensionRequest.requestState = ExtensionRequestState.Rejected;
                    extensionRequest.resolution = {
                        date: new Date(),
                        message: rejectReason,
                        userId: this.user.id,
                        userName: this.user.displayName,
                        userIconUrl: this.getUserIconUrl(this.user.id)
                    };
                    extensionRequest.lastUpdatedDate = extensionRequest.resolution.date;
                    const index = this.requestedExtensions.value.indexOf(extensionRequest);
                    if (index > -1) {
                        // move this request to first index in list
                        this.requestedExtensions.splice(0, 0, this.requestedExtensions.splice(index, 1)[0]);
                    }

                    if (this.messageService) {
                        this.messageService.addToast({ message: format(Resources.RequestRejected, extensionRequest.extensionName), duration: 3000 });
                    }
                },
                () => {
                    if (this.messageService) {
                        this.messageService.addToast({
                            message: format(Resources.RequestRejectFailed, extensionRequest.extensionName),
                            duration: 3000
                        });
                    }
                }
            );
    };

    public uninstallExtension = (extension: IInstalledExtensionViewModel, reason?: string, reasonCode?: string): void => {
        this.extensionManagementRestClient.uninstallExtensionByName(extension.publisherId, extension.extensionId, reason, reasonCode).then(
            () => {
                // if uninstall Successful remove extension from respective list
                const index = this.installedExtensions.value.indexOf(extension);
                if (index > -1) {
                    this.installedExtensions.splice(index, 1);
                }
                // if uninstalled extension was shared extension, add it to shared extensions list
                if (extension.isSharedExtension) {
                    this.sharedExtensions.push(extension as IExtensionViewModel);
                }

                if (this.messageService) {
                    this.messageService.addToast({
                        message: format(Resources.UninstallExtensionSuccessful, extension.extensionName),
                        duration: 3000
                    });
                }
            },
            () => {
                if (this.messageService) {
                    if (this.messageService) {
                        this.messageService.addToast({
                            message: format(Resources.UninstallExtensionFailed, extension.extensionName),
                            duration: 3000
                        });
                    }
                }
            }
        );
    };

    private buildExtensions = (): void => {
        if (this.extensionsServiceData.sharedExtensions) {
            // intialize data memebers not part of data provider
            const sharedExtensionViewModels = this.processSharedExtensionModels(this.extensionsServiceData.sharedExtensions);
            this.sharedExtensions = new ObservableArray<IExtensionViewModel>(sharedExtensionViewModels);
            // Store reference to extension with identifier as a key in the map
            sharedExtensionViewModels.forEach(extensions => {
                this.extensionsMap[this.createExtensionIdentifier(extensions.publisherId, extensions.extensionId)] = extensions;
            });
        }

        if (this.extensionsServiceData.installedExtensions) {
            const installedExtensionViewModels = this.processInstalledExtenionModels(this.extensionsServiceData.installedExtensions);
            this.installedExtensions = new ObservableArray<IInstalledExtensionViewModel>(installedExtensionViewModels);
            installedExtensionViewModels.forEach(extensions => {
                this.extensionsMap[this.createExtensionIdentifier(extensions.publisherId, extensions.extensionId)] = extensions;
            });
        }
    };

    private buildExtensionRequests = (): void => {
        if (this.extensionsServiceData.requestedExtensions) {
            const extensionIds: string[] = [];

            // list identifiers of extensions requests which do not have published extension data
            for (const requestedExtension of this.extensionsServiceData.requestedExtensions) {
                const id = this.createExtensionIdentifier(requestedExtension.publisherId, requestedExtension.extensionId);
                if (!this.extensionsMap[id]) {
                    extensionIds.push(id);
                }
            }

            if (extensionIds.length > 0) {
                // get published extensions data
                const promise = this.getPublishedExtensions(extensionIds, this.accountToken);
                this.requestedExtensions = new ObservableArray<IRequestedExtensionViewModel>([]);
                promise.then((publishedExtensions: PublishedExtension[]) => {
                    for (const publishedExtension of publishedExtensions) {
                        const extension: IExtensionViewModel = {
                            extensionDetailsUrl: this.getExtensionDetailLink(
                                publishedExtension.publisher.publisherName,
                                publishedExtension.extensionName
                            ),
                            extensionId: publishedExtension.extensionName,
                            extensionIconUrl: this.getExtensionIconUrl(publishedExtension.publisher.publisherName, publishedExtension.extensionName),
                            extensionInstallPageUrl: this.getExtensionInstallLink(
                                publishedExtension.publisher.publisherName,
                                publishedExtension.extensionName
                            ),
                            extensionName: publishedExtension.displayName,
                            isCertifiedPublisher: (publishedExtension.publisher.flags & PublisherFlags.Certified) === PublisherFlags.Certified,
                            lastUpdated: publishedExtension.lastUpdated,
                            publisherId: publishedExtension.publisher.publisherName,
                            publisherName: publishedExtension.publisher.displayName,
                            shortDescription: publishedExtension.shortDescription,
                            version: ""
                        };

                        this.extensionsMap[
                            this.createExtensionIdentifier(publishedExtension.publisher.publisherName, publishedExtension.extensionName)
                        ] = extension;
                    }

                    // build request view data
                    const requestedExtensionViewModels = this.processRequestedExtensionViewModels(this.extensionsServiceData.requestedExtensions);
                    this.requestedExtensions.value = requestedExtensionViewModels;
                    // add requested extensions to it's own map
                    requestedExtensionViewModels.forEach(extensions => {
                        this.requestedExtensionsMap[this.createExtensionIdentifier(extensions.publisherId, extensions.extensionId)] = extensions;
                    });

                    if (this.isLoadingRequestedExtensionsTab.value) {
                        this.isLoadingRequestedExtensionsTab.value = false;
                    }
                });
            } else {
                // build directly if the data is available locally
                // build request view data
                const requestedExtensionViewModels = this.processRequestedExtensionViewModels(this.extensionsServiceData.requestedExtensions);
                this.requestedExtensions = new ObservableArray<IRequestedExtensionViewModel>(requestedExtensionViewModels);

                // add requested extensions to it's own map
                requestedExtensionViewModels.forEach(extensions => {
                    this.requestedExtensionsMap[this.createExtensionIdentifier(extensions.publisherId, extensions.extensionId)] = extensions;
                });

                if (this.isLoadingRequestedExtensionsTab.value) {
                    this.isLoadingRequestedExtensionsTab.value = false;
                }
            }
        }
    };

    private createExtensionIdentifier(publisherId: string, extensionId: string): string {
        return publisherId + "." + extensionId;
    }

    private createExtensionLink(publisherId: string, extensionId: string, type: string): string {
        let extensionUrl;
        const itemPath =
            type +
            "?itemName=" +
            this.createExtensionIdentifier(publisherId, extensionId) +
            "&targetId=" +
            this.hostId +
            "&utm_source=vstsproduct&utm_medium=ExtHubManageList";
        if (this.isHosted) {
            extensionUrl = this.collectionUrl + "_redirect?target=" + encodeURIComponent(combineUrlPaths(this.galleryRootUrl, itemPath));
        } else {
            extensionUrl = combineUrlPaths(this.galleryWebUrl, itemPath);
        }

        return extensionUrl;
    }

    private getExtensionDetailLink(publisherId: string, extensionId: string): string {
        return this.createExtensionLink(publisherId, extensionId, "items");
    }

    private getExtensionIconUrl(publisherName: string, extensionName: string) {
        const iconUrl =
            "_apis/public/gallery/publisher/" +
            publisherName +
            "/extension/" +
            extensionName +
            "/latest/assetbyname/Microsoft.VisualStudio.Services.Icons.Default";
        return this.galleryRootUrl + iconUrl;
    }

    private getExtensionInstallLink(publisherId: string, extensionId: string): string {
        return this.createExtensionLink(publisherId, extensionId, "acquisition");
    }

    private getPublishedExtensions = (extensionNames: string[], accountToken: string | undefined): Promise<PublishedExtension[]> => {
        const query = <ExtensionQuery>{
            filters: [
                <QueryFilter>{
                    criteria: extensionNames
                        .map(n => {
                            return { filterType: ExtensionQueryFilterType.Name, value: n };
                        })
                        .concat(this.getVstsInstallationTargetFilterCriteria())
                }
            ],
            flags: ExtensionQueryFlags.IncludeCategoryAndTags
        };

        return this.galleryRestClient.queryExtensions(query, accountToken).then((result: ExtensionQueryResult) => {
            return <PublishedExtension[]>result.results[0].extensions;
        });
    };

    private getUserIconUrl(id: string): string {
        const iconPath = "_api/_common/IdentityImage?id=" + id;
        if (this.isHosted) {
            return this.collectionUrl + iconPath;
        } else {
            return this.galleryRootUrl + iconPath;
        }
    }

    private getVstsInstallationTargetFilterCriteria(): FilterCriteria[] {
        const vstsInstallationTargets = [
            "Microsoft.VisualStudio.Services",
            "Microsoft.VisualStudio.Services.Resource.Cloud",
            "Microsoft.VisualStudio.Services.Cloud",
            "Microsoft.TeamFoundation.Server",
            "Microsoft.VisualStudio.Services.Integration",
            "Microsoft.VisualStudio.Services.Cloud.Integration",
            "Microsoft.TeamFoundation.Server.Integration"
        ];

        const criteria: FilterCriteria[] = vstsInstallationTargets.map(target => {
            return { filterType: ExtensionQueryFilterType.InstallationTarget, value: target };
        });

        return criteria;
    }

    private installedExtensionComparator = (a: IInstalledExtensionViewModel, b: IInstalledExtensionViewModel) => {
        if (a.reauthorizationRequired && b.reauthorizationRequired) {
            return a.extensionName.localeCompare(b.extensionName, navigator.language);
        } else if (a.reauthorizationRequired) {
            return -1;
        } else if (b.reauthorizationRequired) {
            return 1;
        }

        return a.extensionName.localeCompare(b.extensionName, navigator.language);
    };

    private processInstalledExtenionModels(installedExtensions: IInstalledExtensionModel[]): IInstalledExtensionViewModel[] {
        const installedExtensionViewModels: IInstalledExtensionViewModel[] = [];
        installedExtensions.map((installedExtension: IInstalledExtensionModel) => {
            installedExtensionViewModels.push({
                extensionInstallPageUrl: this.getExtensionInstallLink(installedExtension.publisherId, installedExtension.extensionId),
                extensionIconUrl: this.getExtensionIconUrl(installedExtension.publisherId, installedExtension.extensionId),
                extensionDetailsUrl: this.getExtensionDetailLink(installedExtension.publisherId, installedExtension.extensionId),
                currentVersionsRegistrationId: installedExtension.currentVersionsRegistrationId,
                extensionDisabled: new ObservableValue((installedExtension.installStateFlags & ExtensionStateFlags.Disabled) === 1),
                extensionId: installedExtension.extensionId,
                extensionName: installedExtension.extensionName,
                installStateFlags: installedExtension.installStateFlags,
                isCertifiedPublisher: installedExtension.isCertifiedPublisher,
                isSharedExtension: installedExtension.isSharedExtension,
                lastUpdated: installedExtension.lastUpdated,
                latestAvailableVersion: installedExtension.latestAvailableVersion,
                manifestSource: installedExtension.manifestSource,
                publisherId: installedExtension.publisherId,
                publisherName: installedExtension.publisherName,
                reauthorizationRequired:
                    (installedExtension.installStateFlags & (ExtensionStateFlags.NeedsReauthorization | ExtensionStateFlags.AutoUpgradeError)) != 0,
                shortDescription: installedExtension.shortDescription,
                version: installedExtension.version
            });
        });

        installedExtensionViewModels.sort(this.installedExtensionComparator);
        return installedExtensionViewModels;
    }

    private processRequestedExtensionViewModels(requestedExtensions: IRequestedExtensionModel[]): IRequestedExtensionViewModel[] {
        const requestedExtensionViewModels: IRequestedExtensionViewModel[] = [];
        requestedExtensions.map((requestedExtension: IRequestedExtensionModel) => {
            const publishedExtension = this.extensionsMap[
                this.createExtensionIdentifier(requestedExtension.publisherId, requestedExtension.extensionId)
            ];
            if (publishedExtension) {
                const requestViewModels: IExtensionActionViewModel[] = [];
                let resolutionViewModel: IExtensionActionViewModel | undefined = undefined;

                requestedExtension.requests.forEach(request => {
                    requestViewModels.push({
                        date: request.date,
                        message: request.message,
                        userIconUrl: this.getUserIconUrl(request.userId),
                        userId: request.userId,
                        userName: request.userName
                    });
                });

                requestViewModels.sort((a, b) => {
                    return a.date.getTime() - b.date.getTime();
                });

                const mostRecentRequest = requestViewModels[0];

                if (requestedExtension.resolution) {
                    resolutionViewModel = {
                        date: requestedExtension.resolution.date,
                        message: requestedExtension.resolution.message,
                        userIconUrl: this.getUserIconUrl(requestedExtension.resolution.userId),
                        userId: requestedExtension.resolution.userId,
                        userName: requestedExtension.resolution.userName
                    };
                }

                requestedExtensionViewModels.push({
                    extensionDetailsUrl: publishedExtension.extensionDetailsUrl,
                    extensionIconUrl: publishedExtension.extensionIconUrl,
                    extensionId: requestedExtension.extensionId,
                    extensionInstallPageUrl: this.getExtensionInstallLink(requestedExtension.publisherId, requestedExtension.extensionId),
                    extensionName: publishedExtension.extensionName,
                    isCertifiedPublisher: publishedExtension.isCertifiedPublisher,
                    lastUpdated: publishedExtension.lastUpdated,
                    lastUpdatedDate: requestedExtension.resolution ? requestedExtension.resolution.date : mostRecentRequest.date,
                    version: publishedExtension.version,
                    publisherId: requestedExtension.publisherId,
                    publisherName: publishedExtension.publisherName,
                    requestState: requestedExtension.requestState,
                    requests: requestViewModels,
                    resolution: resolutionViewModel,
                    shortDescription: publishedExtension.shortDescription
                });
            }
        });

        requestedExtensionViewModels.sort((a, b) => {
            return b.lastUpdatedDate.getTime() - a.lastUpdatedDate.getTime();
        });

        return requestedExtensionViewModels;
    }

    private processSharedExtensionModels(sharedExtensions: IExtensionModel[]): IExtensionViewModel[] {
        const sharedExtensionViewModels: IExtensionViewModel[] = [];
        sharedExtensions.map((sharedExtension: IExtensionModel) => {
            sharedExtensionViewModels.push({
                extensionDetailsUrl: this.getExtensionDetailLink(sharedExtension.publisherId, sharedExtension.extensionId),
                extensionIconUrl: this.getExtensionIconUrl(sharedExtension.publisherId, sharedExtension.extensionId),
                extensionId: sharedExtension.extensionId,
                extensionInstallPageUrl: this.getExtensionInstallLink(sharedExtension.publisherId, sharedExtension.extensionId),
                extensionName: sharedExtension.extensionName,
                isCertifiedPublisher: sharedExtension.isCertifiedPublisher,
                lastUpdated: sharedExtension.lastUpdated,
                publisherId: sharedExtension.publisherId,
                publisherName: sharedExtension.publisherName,
                shortDescription: sharedExtension.shortDescription,
                version: sharedExtension.version
            });
        });
        return sharedExtensionViewModels;
    }

    private toggleExtensionState(extension: IInstalledExtensionViewModel): void {
        // created parital installed extension object with required data
        const installedExtension = {
            extensionId: extension.extensionId,
            publisherId: extension.publisherId,
            installState: { flags: extension.installStateFlags ^ ExtensionStateFlags.Disabled },
            version: extension.version
        } as InstalledExtension;
        this.extensionManagementRestClient.updateInstalledExtension(installedExtension).then(
            updatedExtension => {
                extension.installStateFlags = updatedExtension.installState.flags;
                extension.extensionDisabled.value = (updatedExtension.installState.flags & ExtensionStateFlags.Disabled) === 1;

                if (this.messageService) {
                    this.messageService.addToast({
                        message: format(
                            extension.extensionDisabled.value ? Resources.ExtensionDisabled : Resources.ExtensionEnabled,
                            extension.extensionName
                        ),
                        duration: 3000
                    });
                }

                // reload history
                this.getExtensionHistory(extension, true);
            },
            () => {
                if (this.messageService) {
                    this.messageService.addToast({
                        message: format(
                            extension.extensionDisabled.value ? Resources.ExtensionDisableFailed : Resources.ExtensionEnableFailed,
                            extension.extensionName
                        ),
                        duration: 3000
                    });
                }
            }
        );
    }
}

Services.add("IExtensionsService", { serviceFactory: ExtensionsService });
